/**
 * Toast System
 *
 * Shared non-blocking notification surface.
 *
 * Usage:
 *   showToast('Saved');
 *   showToast('Could not save', { variant: 'error' });
 *   showToast('Deleted', { action: { label: 'Undo', onClick: restore } });
 *
 * Options:
 *   variant: 'info' | 'success' | 'error'  (default 'info')
 *   duration: ms before auto-dismiss       (default 4000; 0 = sticky)
 *   action: { label, onClick }             (optional button inside toast)
 */

const CONTAINER_ID = 'toast-container';

function getContainer() {
  let el = document.getElementById(CONTAINER_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = CONTAINER_ID;
    el.className = 'toast-container';
    el.setAttribute('role', 'region');
    el.setAttribute('aria-label', 'Notifications');
    document.body.appendChild(el);
  }
  return el;
}

/**
 * Show a toast. Returns a dismiss() function for programmatic removal.
 * @param {string} message
 * @param {{variant?: 'info'|'success'|'error', duration?: number, action?: {label: string, onClick: Function}}} [opts]
 * @returns {() => void} dismiss
 */
export function showToast(message, opts = {}) {
  const { variant = 'info', duration = 4000, action } = opts;

  const container = getContainer();
  const el = document.createElement('div');
  el.className = `toast toast--${variant}`;
  el.setAttribute('role', variant === 'error' ? 'alert' : 'status');

  const text = document.createElement('span');
  text.className = 'toast__text';
  text.textContent = message;
  el.appendChild(text);

  let timerId = null;

  const dismiss = () => {
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }
    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  };

  if (action && typeof action.onClick === 'function') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = action.label;
    btn.className = 'toast__action';
    btn.addEventListener('click', () => {
      try { action.onClick(); } finally { dismiss(); }
    });
    el.appendChild(btn);
  }

  container.appendChild(el);

  if (duration > 0) {
    timerId = setTimeout(dismiss, duration);
  }

  return dismiss;
}

/**
 * Convenience: success toast.
 */
export function showSuccess(message, opts = {}) {
  return showToast(message, { ...opts, variant: 'success' });
}

/**
 * Convenience: error toast.
 */
export function showError(message, opts = {}) {
  return showToast(message, { ...opts, variant: 'error' });
}

/**
 * Remove every currently visible toast.
 */
export function dismissAll() {
  const container = document.getElementById(CONTAINER_ID);
  if (!container) return;
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }
}
